import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';

interface DeviceInfo {
  browser: string;
  os: string;
  device: string;
  screen: string;
}

export const useSessionTracking = () => {
  const { user } = useAuth();
  const sessionIdRef = useRef<string>();
  const heartbeatRef = useRef<ReturnType<typeof setInterval>>();
  const isActiveRef = useRef(true);

  // Generate unique session ID
  const generateSessionId = () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  // Get device info
  const getDeviceInfo = (): DeviceInfo => {
    const ua = navigator.userAgent;
    return {
      browser: getBrowser(ua),
      os: getOS(ua),
      device: getDevice(ua),
      screen: `${screen.width}x${screen.height}`
    };
  };

  const getBrowser = (ua: string): string => {
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown';
  };

  const getOS = (ua: string): string => {
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iOS')) return 'iOS';
    return 'Unknown';
  };

  const getDevice = (ua: string): string => {
    if (/Mobile|Android|iPhone|iPad/.test(ua)) return 'Mobile';
    if (/Tablet|iPad/.test(ua)) return 'Tablet';
    return 'Desktop';
  };

  // Hobby deploy'da backend IP route'u yok; session kaydında anonim IP kullan.
  const getClientIP = async (): Promise<string> => 'unknown';

  // Update session status
  const updateSession = async (status: 'online' | 'away' | 'offline'): Promise<void> => {
    if (!user || !sessionIdRef.current) return;
    
    // Temp session kontrolü - skip session tracking
    const tempSession = localStorage.getItem('temp_user_session');
    const customAuth = localStorage.getItem('custom_auth_token');
    if (tempSession || customAuth) {
      return;
    }

    try {
      const deviceInfo = getDeviceInfo();
      const ipAddress = await getClientIP();

      // Safe upsert function kullan
      const { error } = await supabase.rpc('safe_upsert_user_session', {
        p_user_id: user.id,
        p_session_id: sessionIdRef.current,
        p_status: status,
        p_ip_address: ipAddress,
        p_user_agent: navigator.userAgent,
        p_device_info: deviceInfo
      });
      
      if (error && error.code !== '23505') { // Ignore duplicate key errors
        console.warn('Session upsert failed:', error.code);
      }
    } catch (error) {
      // Sessizce logla, hata verme
      console.warn('Session update failed:', error);
    }
  };

  // Start session tracking
  const startSession = async (): Promise<void> => {
    if (!user) return;

    sessionIdRef.current = generateSessionId();
    await updateSession('online');

    // Heartbeat every 30 seconds
    heartbeatRef.current = setInterval(() => {
      if (isActiveRef.current) {
        updateSession('online');
      } else {
        updateSession('away');
      }
    }, 30000);
  };

  // End session
  const endSession = async (): Promise<void> => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
    }
    await updateSession('offline');
  };

  // Handle visibility change
  const handleVisibilityChange = (): void => {
    isActiveRef.current = !document.hidden;
    if (document.hidden) {
      updateSession('away');
    } else {
      updateSession('online');
    }
  };

  // Handle window focus/blur
  const handleFocus = (): void => {
    isActiveRef.current = true;
    updateSession('online');
  };

  const handleBlur = (): void => {
    isActiveRef.current = false;
    updateSession('away');
  };

  // Handle page unload
  const handleBeforeUnload = (): void => {
    if (user && sessionIdRef.current) {
      void updateSession('offline');
    }
  };

  useEffect(() => {
    if (user) {
      startSession();

      // Event listeners
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);
      window.addEventListener('blur', handleBlur);
      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
        window.removeEventListener('blur', handleBlur);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        endSession();
      };
    }
    return undefined;
  }, [user]);

  return {
    sessionId: sessionIdRef.current,
    updateSession
  };
};
